@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import com.fsryan.foundation.data.FSSyncEvent
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * The job-description pipeline operations of the Wagemix API, all on behalf of the signed-in user [uid].
 *
 * One nested sealed triple per operation (sync data source playbook, Step 2). Every event carries
 * the operation's inputs and nothing it produced: outcomes are written to a local data source and
 * observed there. `Failure` adds the error. Constructors are public because the backend variant
 * module constructs these events (controlled-construction playbook: sync-event lifecycle variants).
 */
sealed interface WMJobDescriptionSyncEvent<out E: WMJobDescriptionSyncEvent<E>>: FSSyncEvent<E> {

    /** Upload a file and follow the pipeline to its end; progress lands in the user's local context under [eventStreamId]. */
    sealed interface UploadFile<out E: UploadFile<E>>: WMJobDescriptionSyncEvent<E> {
        val uid: String
        val eventStreamId: String
        val fileName: String

        data class Initial(
            override val uid: String,
            override val eventStreamId: String,
            override val fileName: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UploadFile<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val eventStreamId: String,
            override val fileName: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UploadFile<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val eventStreamId: String,
            override val fileName: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UploadFile<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Submit a posting URL and follow the pipeline to its end. */
    sealed interface UploadUrl<out E: UploadUrl<E>>: WMJobDescriptionSyncEvent<E> {
        val uid: String
        val eventStreamId: String
        val url: String

        data class Initial(
            override val uid: String,
            override val eventStreamId: String,
            override val url: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UploadUrl<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val eventStreamId: String,
            override val url: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UploadUrl<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val eventStreamId: String,
            override val url: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UploadUrl<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Re-run the pipeline for the stored mix [mixId]. */
    /**
     * Run a failed upload's pipeline again. Success means it restarted; its progress and outcome arrive through the
     * mirror of the user's uploads, like any upload's.
     */
    sealed interface RetryUpload<out E: RetryUpload<E>>: WMJobDescriptionSyncEvent<E> {
        val uid: String
        val uploadId: String

        data class Initial(
            override val uid: String,
            override val uploadId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RetryUpload<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val uploadId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RetryUpload<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val uploadId: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RetryUpload<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Delete a failed upload and its files; the mirror of the user's uploads drops it. */
    sealed interface DeleteUpload<out E: DeleteUpload<E>>: WMJobDescriptionSyncEvent<E> {
        val uid: String
        val uploadId: String

        data class Initial(
            override val uid: String,
            override val uploadId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DeleteUpload<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val uploadId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DeleteUpload<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val uploadId: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DeleteUpload<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    sealed interface Reevaluate<out E: Reevaluate<E>>: WMJobDescriptionSyncEvent<E> {
        val uid: String
        val eventStreamId: String
        val mixId: String

        data class Initial(
            override val uid: String,
            override val eventStreamId: String,
            override val mixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Reevaluate<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val eventStreamId: String,
            override val mixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Reevaluate<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val eventStreamId: String,
            override val mixId: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Reevaluate<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** The stored files behind [mixIds], with fresh preview and download links; they land in the local context. */
    sealed interface FetchFileUrls<out E: FetchFileUrls<E>>: WMJobDescriptionSyncEvent<E> {
        val uid: String
        val mixIds: Set<String>

        data class Initial(
            override val uid: String,
            override val mixIds: Set<String>,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchFileUrls<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val mixIds: Set<String>,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchFileUrls<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val mixIds: Set<String>,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchFileUrls<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }
}
